import { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  where,
} from "firebase/firestore";
import { db } from "../../Security/firebase-config";
import "./ReviewedButton.css";

const ROLE_ORDER = ["player", "sub", "analyst", "coach", "manager", "trial", "spectator"];
const ROLE_LABEL = {
  player: "Players",
  sub: "Subs",
  analyst: "Analysts",
  coach: "Coaches",
  manager: "Managers",
  trial: "Trialing",
  spectator: "Spectators",
};

const normalizeRole = (r) => {
  const normalized = (r || "Player").trim().toLowerCase();
  if (normalized === "trialing" || normalized === "trials") return "trial";
  return normalized;
};
const rankOf = (roleLower) => {
  const i = ROLE_ORDER.indexOf(roleLower || "");
  return i === -1 ? 999 : i;
};

function asUsername(v, uidToUsername) {
  if (!v) return null;
  if (uidToUsername && uidToUsername.has(v)) return uidToUsername.get(v);
  return v;
}

export default function ReviewedButton({ teamId, mapId, videoId }) {
  const [displayUsers, setDisplayUsers] = useState([]);
  const [playersCaughtUp, setPlayersCaughtUp] = useState(0);
  const [playersTotal, setPlayersTotal] = useState(0);

  useEffect(() => {
    if (!teamId || !mapId || !videoId) return;

    let cancelled = false;

    const run = async () => {
      // 1) Team members + roles
      const teamSnap = await getDoc(doc(db, "teams", teamId));
      const team = teamSnap.data() || {};
      const memberIds = team.members || [];
      const teamRoles = team.teamRoles || {};

      const members = (
        await Promise.all(
          memberIds.map(async (uid) => {
            const uSnap = await getDoc(doc(db, "uids", uid));
            const uname = uSnap.exists() ? uSnap.data()?.username : null;
            if (!uname) return null;

            const role = teamRoles[uid] || "Player";
            return { uid, username: uname, role, roleLower: normalizeRole(role) };
          })
        )
      ).filter(Boolean);

      const usernames = members.map((m) => m.username);
      const uidToUsername = new Map(members.map((m) => [m.uid, m.username]));
      const roleByUsername = new Map(members.map((m) => [m.username, m.roleLower]));

      // 2) Get matchId from video doc (if any)
      let matchId = null;
      try {
        const videoSnap = await getDoc(
          doc(db, "teams", teamId, "maps", mapId, "videos", videoId)
        );
        if (videoSnap.exists()) matchId = videoSnap.data()?.matchId || null;
      } catch {
        matchId = null;
      }

      const matchNotesColl = matchId
        ? collection(db, "teams", teamId, "maps", mapId, "matches", matchId, "notes")
        : null;

      const legacyNotesColl = collection(
        db,
        "teams",
        teamId,
        "maps",
        mapId,
        "videos",
        videoId,
        "notes"
      );

      // Prefer match notes if matchId exists
      let mode = "legacy"; // "match" | "legacy"
      let finalNotesDocs = [];

      if (matchNotesColl) {
        const matchSnap = await getDocs(query(matchNotesColl, orderBy("createdAt", "asc")));
        if (matchSnap.docs.length > 0) {
          mode = "match";
          finalNotesDocs = matchSnap.docs;
        } else {
          const legacySnap = await getDocs(query(legacyNotesColl, orderBy("createdAt", "asc")));
          mode = "legacy";
          finalNotesDocs = legacySnap.docs;
        }
      } else {
        const legacySnap = await getDocs(query(legacyNotesColl, orderBy("createdAt", "asc")));
        mode = "legacy";
        finalNotesDocs = legacySnap.docs;
      }

      // 3) Compute unreviewed counts
      const unreviewedByUser = new Map(usernames.map((u) => [u, 0]));
      const bump = (uname) => {
        if (!uname) return;
        unreviewedByUser.set(uname, (unreviewedByUser.get(uname) ?? 0) + 1);
      };

      // If we're in match mode, replies live in /allReplies (flat)
      const matchAllRepliesColl =
        mode === "match" && matchId
          ? collection(db, "teams", teamId, "maps", mapId, "matches", matchId, "allReplies")
          : null;

      for (const noteDoc of finalNotesDocs) {
        const note = noteDoc.data() || {};

        const mentions = Array.isArray(note.at) ? note.at : [];
        const viewersRaw = Array.isArray(note.viewers) ? note.viewers : [];
        const viewers = new Set(
          viewersRaw.map((v) => asUsername(v, uidToUsername)).filter(Boolean)
        );

        const createdBy = asUsername(note.createdBy, uidToUsername);

        const mentionsTeam = mentions.includes("@Team");
        const directMentioned = new Set(
          mentions
            .filter((tag) => typeof tag === "string" && tag.startsWith("@") && tag !== "@Team")
            .map((tag) => tag.slice(1))
        );

        // Relevance logic
        for (const uname of usernames) {
          const r = roleByUsername.get(uname) || "player";
          const relevant =
            directMentioned.has(uname) ||
            (mentionsTeam && (r === "player" || r === "analyst" || r === "coach"));

          const hasViewed = viewers.has(uname);

          if (relevant && !hasViewed) bump(uname);
        }

        // Replies logic (THIS WAS CAUSING PERMISSION ERROR)
        if (createdBy && usernames.includes(createdBy)) {
          try {
            if (mode === "match" && matchAllRepliesColl) {
              // ✅ Match replies are stored flat in /allReplies with noteId
              const repliesSnap = await getDocs(
                query(matchAllRepliesColl, where("noteId", "==", noteDoc.id))
              );

              for (const replyDoc of repliesSnap.docs) {
                const reply = replyDoc.data() || {};
                const rViewersRaw = Array.isArray(reply.viewers) ? reply.viewers : [];
                const rViewers = new Set(
                  rViewersRaw.map((v) => asUsername(v, uidToUsername)).filter(Boolean)
                );
                if (!rViewers.has(createdBy)) bump(createdBy);
              }
            } else {
              // ✅ Legacy video replies are nested: /videos/{videoId}/notes/{noteId}/replies
              const repliesColl = collection(legacyNotesColl, noteDoc.id, "replies");
              const repliesSnap = await getDocs(repliesColl);

              for (const replyDoc of repliesSnap.docs) {
                const reply = replyDoc.data() || {};
                const rViewersRaw = Array.isArray(reply.viewers) ? reply.viewers : [];
                const rViewers = new Set(
                  rViewersRaw.map((v) => asUsername(v, uidToUsername)).filter(Boolean)
                );
                if (!rViewers.has(createdBy)) bump(createdBy);
              }
            }
          } catch (e) {
            // If some old data has no access path, don't crash the whole eye button.
            // (But with the correct mode it should no longer throw.)
            // console.warn("ReviewedButton replies read failed:", e);
          }
        }
      }

      // 4) Sorted list
      const list = members
        .map((m) => ({
          username: m.username,
          role: m.role,
          roleLower: m.roleLower,
          unreviewed: unreviewedByUser.get(m.username) ?? 0,
        }))
        .sort((a, b) => {
          const byRole = rankOf(a.roleLower) - rankOf(b.roleLower);
          return byRole !== 0 ? byRole : a.username.localeCompare(b.username);
        });

      if (cancelled) return;

      setDisplayUsers(list);

      const pTotal = list.filter((u) => u.roleLower === "player").length;
      const pCaught = list.filter((u) => u.roleLower === "player" && u.unreviewed === 0).length;
      setPlayersTotal(pTotal);
      setPlayersCaughtUp(pCaught);
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [teamId, mapId, videoId]);

  const isAllPlayersCaughtUp = useMemo(
    () => playersTotal > 0 && playersCaughtUp === playersTotal,
    [playersCaughtUp, playersTotal]
  );

  const grouped = ROLE_ORDER.map((role) => ({
    role,
    label: ROLE_LABEL[role] || role,
    users: displayUsers.filter((u) => u.roleLower === role),
  })).filter((g) => g.users.length > 0);

  return (
    <div
      className={`video-eye-wrapper ${isAllPlayersCaughtUp ? "all-reviewed" : ""}`}
      onClick={(e) => e.stopPropagation()}
      title="Review status by role (Players considered for green state)"
    >
      👁
      <div className="eye-tooltip">
        {grouped.length === 0 ? (
          "No teammates yet"
        ) : (
          grouped.map((group, gi) => (
            <div className="tooltip-group" key={group.role}>
              {gi > 0 && <div className="tooltip-role-divider" />}
              <div className="tooltip-role-label">{group.label}</div>

              {group.users.map((u) => {
                const needsReview = u.unreviewed > 0;
                const className = needsReview
                  ? u.roleLower === "player"
                    ? "tooltip-user missing-player"
                    : "tooltip-user missing-nonplayer"
                  : "tooltip-user ok";

                return (
                  <div key={u.username} className={className}>
                    {u.username}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      <div className="eye-count">
        {`${displayUsers.filter((u) => u.unreviewed === 0).length}/${displayUsers.length}`}
      </div>
    </div>
  );
}
